import {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useRef,
  useState,
} from "react";
import TextareaAutosize from "react-textarea-autosize";
import { TextareaAutosizeProps } from "react-textarea-autosize/dist/declarations/src";
import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { theme } from "@financial-advisory/theme";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { TransparentButton } from "@unlikelyai-magic/ui/buttons";
import { Spacing } from "@unlikelyai-magic/ui/layouts";
import { PromptInputHeader } from "./PromptInputHeader";

const SendButton = styled(TransparentButton)<{ disabled?: boolean }>`
  padding: 0.625rem;
  & > svg {
    width: 1.125rem;
    height: 1.125rem;
    opacity: ${({ disabled }) => (disabled ? 20 : 100)}%;
    color: ${({ theme }) => theme.colors.action.primary.default};
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 100 : 50)}%;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  min-height: 2.5rem;
  padding-left: 0.5rem;
`;

const PromptTextArea = styled(TextareaAutosize, {
  shouldForwardProp: isPropValid,
})<{ placeholderColor: string }>`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.primary.default};
  line-height: 1.5rem;
  background: transparent;
  resize: none;

  ::placeholder {
    font-weight: 400;
    color: ${({ placeholderColor }) => placeholderColor};
  }

  &,
  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    outline: none;
  }
`;

export type PromptInputProps = Pick<
  TextareaAutosizeProps,
  "className" | "placeholder" | "maxRows" | "minRows"
> & {
  onSubmit: (text: string) => void;
  initialValue?: string;
  header?: ReactNode;
  placeholderColor?: string;
  isDisabled?: boolean;
};

export const PromptInput = styled(
  ({
    placeholder = "Type something...",
    initialValue = "",
    onSubmit,
    header,
    isDisabled,
    maxRows = 5,
    placeholderColor = theme.colors.text.secondary.default,

    ...props
  }: PromptInputProps) => {
    const [value, setValue] = useState<string>(initialValue);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
    };

    const handleSubmit = async (event: FormEvent) => {
      event.preventDefault();
      if (!value.trim() || isDisabled) return;
      onSubmit(value);
      setValue("");
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        handleSubmit(event);
      }
    };

    const handleFormClick = () => {
      textAreaRef.current?.focus();
    };

    return (
      <form onSubmit={handleSubmit} onClick={handleFormClick} {...props}>
        {header && <PromptInputHeader>{header}</PromptInputHeader>}
        <Spacing gap="3xs" direction="horizontal" items="flex-end">
          <InputWrapper>
            <PromptTextArea
              ref={textAreaRef}
              maxRows={maxRows}
              placeholder={placeholder}
              placeholderColor={placeholderColor}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              autoFocus={true}
            />
          </InputWrapper>
          <SendButton
            icon={<PaperAirplaneIcon />}
            disabled={!value || isDisabled}
            onClick={handleSubmit}
          />
        </Spacing>
      </form>
    );
  }
)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacings.sm};
  gap: ${({ theme }) => theme.spacings.sm};
  cursor: text;
`;
